import { useEffect, useState } from 'react';
import { LAYOUT_LIMITS } from '@jerome2/shared';
import { useStore } from '../lib/store.ts';
import { app, saveSettings } from '../state/app.ts';
import { chatStore, loadChats } from '../state/chat.ts';
import { markedChat, pickChat, startChat } from '../state/chatTabs.ts';
import { fitWidth, pixelsText, useLayout, widthBounds } from '../state/layout.ts';
import { tabs, type PaneId } from '../state/tabs.ts';
import { errorToast } from '../state/toast.ts';
import { Resizer } from '../ui/Resizer.tsx';
import { ChatList, ChatStrip } from '../chat/ChatList.tsx';
import { DropZone } from '../chat/DropZone.tsx';
import { handFiles } from '../chat/files.ts';

// De gesprekkenlijst als vaste kolom van de schil op de desktop (ticket 28083, 30 oktober 2026: de tabbalk begint
// rechts naast de kolom met chats): links van de panelen, over de volle hoogte onder de bovenbalk en op elk tabblad, want de
// gesprekken staan centraal. Dezelfde lijst en strook als in het chatscherm op de telefoon (ChatList, ChatStrip), met dezelfde
// instelling om in en uit te klappen (`settings.chat.listVisible`). Een klik op een gesprek en Nieuw gesprek kiezen het tabblad
// (state/chatTabs.ts); het gemarkeerde gesprek is dat van het actieve chattabblad van het actieve paneel. Slepen of plakken van
// bestanden in de kolom opent een nieuw gesprek met de bestanden in de lade. Onder 1240 px ruimte wijkt de lijst tot de strook:
// op een tabblad zonder chat (`crowded`, besluit b op 28083: Mail en Agenda houden hun breedte) en op een chattabblad zolang het
// mailpaneel naast het gesprek open is (`listSuppressed`). Wie de lijst op de strook toch uitklapt, houdt hem tot de reden
// verandert (genoeg ruimte, een chattabblad actief, het mailpaneel dicht), zonder dat de instelling verandert.
// De breedte van de lijst stelt de gebruiker in met de greep op de rechterrand (ticket 28376, `settings.layout.chatColumn`, 220 tot
// 480 px en nooit zo breed dat de panelen minder dan 480 px houden); de strook van 44 px heeft geen greep. Het wijken rekent
// met de ruimte van kolom en panelen samen, en die verandert niet met de breedte van de kolom: een bredere lijst wijkt dus bij
// dezelfde ruimte als voorheen, en in- of uitklappen laat de reden niet heen en weer springen.

/** `crowded`: het actieve tabblad is geen chat en kolom en panelen hebben samen minder dan 1240 px (columnCrowded in de schil).
 * `space`: de breedte van kolom en panelen samen, gemeten in de schil; de lijst laat de panelen daarvan minstens 480 px. */
export function ChatColumn(props: { pane: PaneId; crowded: boolean; space: number }) {
  const listVisible = useStore(app, (s) => s.settings.chat.listVisible);
  const suppressed = useStore(chatStore, (s) => Boolean(s.listSuppressed?.length));
  const chatId = useStore(tabs, (s) => markedChat(s, props.pane));
  const [archived, setArchived] = useState(false);
  // Waarom de lijst nu wijkt; null als hij er mag staan.
  const reason = props.crowded ? 'ruimte' : suppressed ? 'mailpaneel' : null;
  const [forced, setForced] = useState(false);

  // De kolom staat er ook zonder chattabblad (Mail, Agenda): de gesprekken zelf ophalen.
  useEffect(() => {
    void loadChats('chat').catch((e) => errorToast(e, 'Gesprekken niet geladen'));
  }, []);
  // Uitklappen tegen de reden in geldt tot die reden verandert.
  useEffect(() => setForced(false), [reason]);

  const save = (visible: boolean) => void saveSettings({ chat: { ...app.get().settings.chat, listVisible: visible } }).catch((e) => errorToast(e, 'Instelling niet bewaard'));
  const collapse = () => {
    setForced(false);
    save(false);
  };
  const expand = () => {
    if (reason) setForced(true);
    if (!listVisible) save(true);
  };
  const create = () => void startChat(props.pane);
  const open = listVisible && (!reason || forced);
  const column = useLayout('chatColumn');
  const bounds = widthBounds('chatColumn', props.space);
  const width = fitWidth('chatColumn', column.value, props.space);

  return (
    <>
      <DropZone className="h-full shrink-0" testId="gesprekkenkolom" label="Laat los voor een nieuw gesprek" compact={!open} onFiles={(files) => handFiles(startChat(props.pane), files)}>
        {open ? <ChatList chatId={chatId} narrow={false} width={width} archived={archived} onArchived={setArchived} onOpen={(id) => void pickChat(id, props.pane)} onNew={create} onCollapse={collapse} /> : <ChatStrip onExpand={expand} onNew={create} />}
      </DropZone>
      {open ? <Resizer label="Breedte gesprekkenkolom" value={width} min={bounds.min} max={bounds.max} defaultValue={LAYOUT_LIMITS.chatColumn.default} side="left" valueText={pixelsText} onChange={column.change} onCommit={column.commit} /> : null}
    </>
  );
}
